/**
 * Thin section navigation for the Form Schema studio, styled to match the
 * clinician visit page sidebar (`OASISNavigation`). Each tab shows a status
 * dot derived from the section's active-template layout state and a three-dot
 * menu of section-level actions. Selecting a tab loads that section's active
 * layout into the inline editor.
 */
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import {
  Clipboard,
  ClipboardPaste,
  FileJson,
  FileText,
  Layers,
  Loader2,
  MoreHorizontal,
  Sparkles,
} from 'lucide-react';
import type {
  SectionGenerationStatus,
  SectionLayoutState,
} from './formSchemaTypes';
import { SectionConsistencyBadge } from './SectionConsistencyBadge';
import { SectionLayoutStatusBadge } from './SectionLayoutStatusBadge';
import type { SectionConsistency } from './sectionConsistency';

interface FormSchemaSectionNavProps {
  /** Section ids in display order. */
  sections: string[];
  /** Resolves a section id to its user-facing label. */
  labelFor: (sectionId: string) => string;
  selectedSection: string | null;
  onSelect: (sectionId: string) => void;
  getLayoutState: (sectionId: string) => SectionLayoutState;
  /**
   * Cross-form design consistency for the section (EMR-1410). Separate from
   * {@link getLayoutState}, which reports whether a layout exists and is
   * valid, not whether it matches the other forms sharing its questions.
   * Omit to hide the marker entirely.
   */
  getConsistency?: (sectionId: string) => SectionConsistency;
  getGenerationStatus: (
    sectionId: string
  ) => SectionGenerationStatus | undefined;
  /** True while an AI chat/fix request is in flight for the section. */
  getChatPending: (sectionId: string) => boolean;
  /** True when the section has unsaved working-copy edits. */
  getIsDirty: (sectionId: string) => boolean;
  /**
   * True when the section has an MCP-pushed template not yet opened in the
   * editor (EMR-277 live refresh). Clears once that specific template becomes
   * the open editor target — see `latestUnacknowledgedRemotePush`.
   */
  getHasRemotePush: (sectionId: string) => boolean;
  /** Tooltip for the remote-push dot, e.g. "New layout pushed · 2:14 PM". `undefined` when {@link getHasRemotePush} is false. */
  getRemotePushTooltip: (sectionId: string) => string | undefined;
  onGenerate: (sectionId: string) => void;
  onGenerateCustom: (sectionId: string) => void;
  onAddFromClipboard: (sectionId: string) => void;
  onCopyLayout: (sectionId: string) => void;
  onViewQuestions: (sectionId: string) => void;
  onOpenLayoutJson: (sectionId: string) => void;
  onOpenVersions: (sectionId: string) => void;
}

export function FormSchemaSectionNav({
  sections,
  labelFor,
  selectedSection,
  onSelect,
  getLayoutState,
  getConsistency,
  getGenerationStatus,
  getChatPending,
  getIsDirty,
  getHasRemotePush,
  getRemotePushTooltip,
  onGenerate,
  onGenerateCustom,
  onAddFromClipboard,
  onCopyLayout,
  onViewQuestions,
  onOpenLayoutJson,
  onOpenVersions,
}: FormSchemaSectionNavProps) {
  return (
    <div className='w-48 flex-shrink-0 pr-3 border-r border-border'>
      <div className='space-y-0.5'>
        <span className='mb-1 block px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
          Sections
        </span>
        {sections.map(section => {
          const isActive = selectedSection === section;
          const layoutState = getLayoutState(section);
          const consistency = getConsistency?.(section);
          const generation = getGenerationStatus(section);
          const chatPending = getChatPending(section);
          const busy = generation?.state === 'loading' || chatPending;
          const dirty = getIsDirty(section);
          const hasLayout = layoutState.kind !== 'missing';
          return (
            <div
              key={section}
              className={`group flex items-center gap-1 rounded-md pr-0.5 ${
                isActive
                  ? 'bg-primary/10'
                  : 'hover:bg-muted focus-within:bg-muted'
              }`}
            >
              <button
                type='button'
                onClick={() => onSelect(section)}
                className={`flex min-w-0 flex-1 items-center justify-between gap-2 px-2.5 py-2 text-left text-xs leading-snug transition-colors ${
                  isActive
                    ? 'font-semibold text-primary'
                    : 'text-muted-foreground group-hover:text-foreground'
                }`}
              >
                <span className='min-w-0 flex-1 truncate'>
                  {labelFor(section)}
                </span>
                <span className='flex shrink-0 items-center gap-1'>
                  {getHasRemotePush(section) ? (
                    <span
                      className='h-1.5 w-1.5 rounded-full bg-teal-500'
                      title={getRemotePushTooltip(section)}
                    />
                  ) : null}
                  {dirty ? (
                    <span
                      className='h-1.5 w-1.5 rounded-full bg-amber-500'
                      title='Unsaved changes'
                    />
                  ) : null}
                  {busy ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin text-primary' />
                  ) : (
                    <>
                      <SectionLayoutStatusBadge state={layoutState} />
                      {consistency ? (
                        <SectionConsistencyBadge state={consistency} />
                      ) : null}
                    </>
                  )}
                </span>
              </button>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-6 w-6 shrink-0 p-0 opacity-60 hover:opacity-100'
                    aria-label={`Actions for ${labelFor(section)}`}
                    onClick={() => onSelect(section)}
                  >
                    <MoreHorizontal className='h-3.5 w-3.5' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onClick={() => onGenerate(section)}
                    disabled={busy}
                  >
                    <Sparkles className='h-3.5 w-3.5 mr-2' />
                    Generate with AI
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onGenerateCustom(section)}
                    disabled={busy}
                  >
                    <Sparkles className='h-3.5 w-3.5 mr-2' />
                    Generate with custom prompt…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onAddFromClipboard(section)}>
                    <ClipboardPaste className='h-3.5 w-3.5 mr-2' />
                    Paste layout
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onCopyLayout(section)}
                    disabled={!hasLayout}
                  >
                    <Clipboard className='h-3.5 w-3.5 mr-2' />
                    Copy layout
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onViewQuestions(section)}>
                    <FileText className='h-3.5 w-3.5 mr-2' />
                    View questions
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenLayoutJson(section)}>
                    <FileJson className='h-3.5 w-3.5 mr-2' />
                    Layout JSON…
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenVersions(section)}>
                    <Layers className='h-3.5 w-3.5 mr-2' />
                    Versions…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>
    </div>
  );
}
